import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import api from "@/api";
import { toast } from "sonner";
import { AlertTriangle, BellRing, CalendarClock, CheckCircle, ClipboardList, Clock, Phone, RefreshCw, Search } from "lucide-react";

const FILTERS = [
  { id: "all", label: "Tous" },
  { id: "late", label: "En retard" },
  { id: "due_now", label: "À compléter" },
  { id: "scheduled", label: "Programmés" },
  { id: "completed", label: "Complétés" },
  { id: "completed_late", label: "Complétés en retard" },
];

const STATUS = {
  late: { label: "En retard", cls: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertTriangle },
  due_now: { label: "À compléter", cls: "bg-blue-50 text-blue-800 border-blue-200", icon: ClipboardList },
  scheduled: { label: "Programmé", cls: "bg-slate-50 text-slate-700 border-slate-200", icon: CalendarClock },
  completed: { label: "Complété", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle },
  completed_late: { label: "Complété en retard", cls: "bg-orange-50 text-orange-800 border-orange-200", icon: CheckCircle },
  configuration_issue: { label: "À configurer", cls: "bg-rose-50 text-rose-800 border-rose-200", icon: AlertTriangle },
};

export default function NurseProFollowup() {
  const [data, setData] = useState({ summary: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [reminding, setReminding] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get("/studies/pro-followup");
      setData(res.data || { summary: {}, items: [] });
    } catch {
      toast.error("Erreur chargement du suivi PRO");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.items || []).filter((item) => {
      if (filter !== "all" && item.status !== filter) return false;
      if (!q) return true;
      return [item.patientName, item.questionnaireName, item.studyName, item.cancerType]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [data.items, filter, search]);

  const remind = async (item) => {
    if (!item.questionnaireCode || ["completed", "completed_late", "scheduled"].includes(item.status)) return;
    try {
      setReminding(item.id);
      const res = await api.post("/studies/pro-followup/remind", {
        patientId: item.patientId,
        enrollmentId: item.enrollmentId,
        visitName: item.visitName,
        questionnaireCode: item.questionnaireCode,
        channel: "in_app_pwa",
      });
      toast.success(res.data?.pushSent ? "Relance envoyée avec PWA" : "Relance In-App créée");
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur relance questionnaire");
    } finally {
      setReminding(null);
    }
  };

  return (
    <ProfessionalLayout title="Suivi questionnaires PRO">
      <div className="space-y-5" data-testid="nurse-pro-followup-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Suivi questionnaires PRO
            </h1>
            <p className="mt-1 text-sm text-slate-500">Questionnaires visibles, en retard, programmés et complétés.</p>
          </div>
          <button
            onClick={fetchData}
            data-testid="pro-followup-refresh-button"
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <RefreshCw className="h-4 w-4" /> Actualiser
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="pro-followup-summary-cards">
          <SummaryCard label="À compléter" value={data.summary?.dueNow || 0} tone="blue" />
          <SummaryCard label="En retard" value={data.summary?.late || 0} tone="amber" />
          <SummaryCard label="Programmés" value={data.summary?.scheduled || 0} tone="slate" />
          <SummaryCard label="Complétés" value={data.summary?.completed || 0} tone="emerald" />
          <SummaryCard label="Ouverts" value={data.summary?.totalOpen || 0} tone="rose" />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher patient, étude, questionnaire..."
                data-testid="pro-followup-search-input"
                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none transition focus:border-emerald-300 focus:bg-white focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 lg:pb-0" data-testid="pro-followup-filter-tabs">
              {FILTERS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setFilter(tab.id)}
                  data-testid={`pro-followup-filter-${tab.id}`}
                  className={`whitespace-nowrap rounded-xl px-3 py-2 text-xs font-bold transition ${filter === tab.id ? "bg-emerald-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="py-14 text-center text-sm text-slate-400" data-testid="pro-followup-loading">Chargement...</div>
          ) : visibleItems.length === 0 ? (
            <div className="py-14 text-center text-sm text-slate-400" data-testid="pro-followup-empty">Aucun questionnaire dans ce filtre.</div>
          ) : (
            <div className="mt-4 overflow-hidden rounded-xl border border-slate-100" data-testid="pro-followup-table">
              <div className="hidden grid-cols-[1.3fr_1.4fr_1fr_0.8fr_0.9fr_1.2fr] gap-3 bg-slate-50 px-4 py-3 text-xs font-bold uppercase text-slate-500 lg:grid">
                <span>Patient</span><span>Questionnaire</span><span>Visite</span><span>Prévu</span><span>Statut</span><span>Action</span>
              </div>
              <div className="divide-y divide-slate-100">
                {visibleItems.map((item) => <FollowupRow key={item.id} item={item} reminding={reminding === item.id} onRemind={() => remind(item)} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </ProfessionalLayout>
  );
}

function SummaryCard({ label, value, tone }) {
  const colors = {
    blue: "bg-blue-50 text-blue-800 border-blue-100",
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    slate: "bg-slate-50 text-slate-800 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
    rose: "bg-rose-50 text-rose-800 border-rose-100",
  };
  return (
    <div className={`rounded-2xl border p-4 ${colors[tone]}`} data-testid={`pro-followup-summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-xs font-bold uppercase opacity-75">{label}</p>
      <p className="mt-2 text-3xl font-black leading-none">{value}</p>
    </div>
  );
}

function FollowupRow({ item, reminding, onRemind }) {
  const meta = STATUS[item.status] || STATUS.configuration_issue;
  const Icon = meta.icon;
  const canRemind = item.questionnaireCode && ["late", "due_now"].includes(item.status);
  return (
    <div className="grid gap-3 px-4 py-4 lg:grid-cols-[1.3fr_1.4fr_1fr_0.8fr_0.9fr_1.2fr] lg:items-center" data-testid={`pro-followup-row-${item.id}`}>
      <div className="min-w-0">
        <Link to={`/nurse/patients/${item.patientId}`} className="font-bold text-slate-900 hover:text-emerald-700" data-testid={`pro-followup-patient-link-${item.id}`}>
          {item.patientName || "Patient"}
        </Link>
        <p className="mt-0.5 text-xs text-slate-500">{item.cancerType || "Pathologie non renseignée"}</p>
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-800" data-testid={`pro-followup-questionnaire-${item.id}`}>{item.questionnaireName}</p>
        <p className="mt-0.5 truncate text-xs text-slate-500">{item.studyName || "Étude"}</p>
      </div>
      <div className="text-sm text-slate-600">
        <span className="font-semibold">{item.visitName}</span>
        <p className="text-xs text-slate-400">Fenêtre ± {item.windowDays || 3} j</p>
      </div>
      <div className="text-sm text-slate-600" data-testid={`pro-followup-due-date-${item.id}`}>
        {item.dueDate || "—"}
        {item.daysLate ? <p className="text-xs font-bold text-amber-700">+{item.daysLate} j</p> : null}
      </div>
      <div>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold ${meta.cls}`} data-testid={`pro-followup-status-${item.id}`}>
          <Icon className="h-3.5 w-3.5" /> {meta.label}
        </span>
        {item.lastReminderAt && <p className="mt-1 text-[11px] text-slate-400">Relance : {new Date(item.lastReminderAt).toLocaleDateString("fr-FR")}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {item.patientPhone && canRemind && (
          <a href={`tel:${item.patientPhone}`} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50" data-testid={`pro-followup-call-${item.id}`}>
            <Phone className="h-3.5 w-3.5" /> Appeler
          </a>
        )}
        {canRemind ? (
          <button
            onClick={onRemind}
            disabled={reminding}
            data-testid={`pro-followup-remind-${item.id}`}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60"
          >
            <BellRing className="h-3.5 w-3.5" /> {reminding ? "Relance..." : "Relancer"}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-lg bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500">
            <Clock className="h-3.5 w-3.5" /> Suivi
          </span>
        )}
      </div>
    </div>
  );
}